import { toCanvas } from "html-to-image";
import { type Card } from "../model/types.ts";
import { type Highlight } from "../model/highlights.ts";
import { paintHighlights } from "../edit/previewHighlights.ts";
import { renderMarkdown } from "./markdown.ts";

/**
 * Snapshot pipeline: Card.markdown -> HTML (markdown-it) -> off-screen DOM ->
 * bitmap (html-to-image). The returned canvas is uploaded to a Pixi texture by
 * the caller. This is the only expensive operation in the system and it runs
 * lazily (on first view / content change / zoom-tier crossing), never per frame.
 *
 * Sólo pasan por acá los nodos `file`: son los que tienen markdown de verdad.
 * El texto suelto y el de las formas se dibujan en el DOM, sin rasterizar nada
 * (ver `canvasText.ts`).
 */

const CARD_CSS = `
  .card-snap {
    box-sizing: border-box;
    background: #f5f5f5;
    /* Mismo radio que los grupos (GROUP_RADIUS = 4 en GroupView): la textura
       rasterizada tapa el fondo vectorial, así que sin esto las esquinas se
       verían cuadradas. El overflow hidden ya recorta el contenido. */
    border-radius: 4px;
    color: #1a1a1f;
    padding: 16px 18px;
    overflow: hidden;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 15px;
    line-height: 1.5;
  }
  .card-snap h1 { font-size: 22px; margin: 0 0 10px; line-height: 1.2; }
  .card-snap h2 { font-size: 18px; margin: 14px 0 8px; line-height: 1.25; }
  .card-snap h3 { font-size: 16px; margin: 12px 0 6px; }
  .card-snap p { margin: 0 0 10px; }
  .card-snap ul, .card-snap ol { margin: 0 0 10px; padding-left: 22px; }
  .card-snap li { margin: 2px 0; }
  .card-snap code {
    background: #ededed; border-radius: 4px; padding: 1px 5px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px;
  }
  .card-snap pre {
    background: #ededed; border-radius: 6px; padding: 10px 12px; overflow: hidden;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px;
  }
  .card-snap pre code { background: none; padding: 0; }
  .card-snap blockquote {
    margin: 0 0 10px; padding: 2px 0 2px 12px;
    border-left: 3px solid #ded9d0; color: #57524a;
  }
  .card-snap a {
    color: #7c6eef;
    font-weight: 500;
    text-decoration-line: underline;
    text-decoration-color: color-mix(in srgb, #7c6eef 48%, transparent);
    text-underline-offset: 2px;
  }
  .card-snap table {
    width: 100%; border-collapse: collapse; margin: 1em 0; font-size: 0.95em;
  }
  .card-snap th, .card-snap td {
    border: 1px solid #ebebeb; padding: 5px 10px; text-align: left;
    vertical-align: top;
  }
  .card-snap th { background: #ededed; font-weight: 600; }
  .card-snap li.task-list-item { list-style: none; margin-left: -1.25em; }
  .card-snap .task-checkbox { vertical-align: -0.1em; margin: 0 0.45em 0 0; }
  .card-snap hr { border: none; border-top: 1px solid #e0e0e0; margin: 12px 0; }
  /* El height auto va junto al tope de ancho: sin él, una imagen más ancha que
     la tarjeta se encoge de ancho pero conserva el alto original y se deforma. */
  .card-snap img { max-width: 100%; height: auto; }

  /* Resaltados. Los tonos van literales (y no como var(--hl-*)) porque esta
     hoja es autocontenida: html-to-image rasteriza el nodo fuera de la vista y
     no debe depender del tema de la app. Mantener en sintonía con el bloque
     .hl de styles.css: los tonos -ink, que son los que se ven en la nota. */
  .card-snap mark.hl {
    border-radius: 3px; padding: 1px 2px; margin: 0 -1px; color: #fff;
    box-decoration-break: clone; -webkit-box-decoration-break: clone;
    background-color: #d5289d;
  }
  .card-snap mark.hl-duda { background-color: #de3535; }
  .card-snap mark.hl-def { background-color: #0075e1; }
  .card-snap mark.hl-idea { background-color: #058a15; }
  .card-snap mark.hl-ejemplo { background-color: #7061ee; }
  .card-snap mark.hl-cita { background-color: #343331; }

`;

let stageEl: HTMLDivElement | null = null;
let styleInjected = false;

function getStage(): HTMLDivElement {
  if (!styleInjected) {
    const style = document.createElement("style");
    style.textContent = CARD_CSS;
    document.head.appendChild(style);
    styleInjected = true;
  }
  if (!stageEl) {
    stageEl = document.createElement("div");
    stageEl.id = "snapshot-stage";
    document.body.appendChild(stageEl);
  }
  return stageEl;
}

/**
 * Deja listas las imágenes del markdown antes de rasterizar.
 *
 * El nodo se arma fuera de la vista y `html-to-image` lo serializa tal como
 * está: un `<img>` con una ruta del vault (`attachments/x.png`) no es algo que
 * el navegador pueda cargar —esa ruta no existe en el servidor— así que la
 * tarjeta salía con el hueco de la imagen rota. Acá se cambia cada `src` por un
 * data URL con los bytes del archivo y se espera a que decodifique: recién ahí
 * el nodo tiene su altura real y la imagen entra en el bitmap.
 */
async function embedImages(
  node: HTMLElement,
  resolve: (src: string) => Promise<string | null>,
): Promise<void> {
  const imgs = [...node.querySelectorAll("img")];
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute("src") ?? "";
      const url = src ? await resolve(src) : null;
      if (!url) {
        // Sin fuente el `<img>` deja un ícono de rota; mejor que no ocupe nada.
        img.remove();
        return;
      }
      img.src = url;
      // Un error de decodificación no debe tumbar el snapshot entero.
      await img.decode().catch(() => {});
    }),
  );
}

/**
 * Rasterize a card's markdown to a canvas.
 * @param tier pixel-ratio multiplier (texels per CSS px). Higher = sharper when
 *   the card is large on screen. The on-screen sprite is always sized to the
 *   card's world dimensions, so a higher tier just packs more texels.
 * @param resolveImage convierte el `src` de una imagen del markdown en un data
 *   URL (ver `embedImages`). Sin esto las imágenes no se dibujan.
 */
export async function snapshotCard(
  card: Card,
  tier: number,
  highlights: readonly Highlight[] = [],
  resolveImage?: (src: string) => Promise<string | null>,
): Promise<HTMLCanvasElement> {
  const stage = getStage();
  const node = document.createElement("div");
  node.className = "card-snap";
  node.style.width = `${card.width}px`;
  node.style.height = `${card.height}px`;
  node.innerHTML = renderMarkdown(card.markdown);
  // Mismo HTML que la vista previa, así que las anclas resuelven igual y una
  // marca se ve idéntica en los dos lados. No se refrescan las anclas: acá
  // se trabaja sobre una copia, el dueño de esos datos es MarkdownView.
  paintHighlights(node, highlights);
  stage.appendChild(node);

  try {
    if (resolveImage) await embedImages(node, resolveImage);
    const canvas = await toCanvas(node, {
      pixelRatio: tier,
      width: card.width,
      height: card.height,
      // Sin backgroundColor: el blanco lo pone el CSS de `.card-snap`, que
      // respeta el border-radius y deja las esquinas transparentes. Pintarlo
      // acá rellenaría todo el rectángulo y las esquinas volverían cuadradas.
      backgroundColor: undefined,
      // Skip cache-busting fetches; our content is self-contained text/CSS.
      cacheBust: false,
    });
    return canvas;
  } finally {
    stage.removeChild(node);
  }
}
